import { useQuery } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { DateTime, Id } from '@/api/utils/sql';
import { useAuth } from '@/context/AuthProvider';

export type ApiRequest_GetInboxConversations = {
  location_id?: Id;
};

export type InboxConversationFromOthers = {
  id: string;
  title: string;
  lastSubject?: string;
  from: string;
  preview: string;
  timestamp: DateTime;
  isRead: boolean;
  sessionId: string;
  source: string;
  participantCount: number;
  type: 'received';
  attachmentCount: number;
  contactRole?: string;
};

export type InboxConversationFromMe = {
  id: string;
  title: string;
  lastSubject?: string;
  to: string;
  preview: string;
  timestamp: DateTime;
  status: 'sent' | 'delivered' | 'pending' | 'failed';
  sessionId: string;
  source: string;
  participantCount: number;
  type: 'sent';
  attachmentCount: number;
  contactRole?: string;
};

export type UnifiedInboxConversation = InboxConversationFromOthers | InboxConversationFromMe;

export type ApiResponse_GetInboxConversations = UnifiedInboxConversation[];

const METHOD = 'GET';
const ENDPOINT = 'conversation/inbox';

export const QUERY_KEY = (params: ApiRequest_GetInboxConversations) =>
  getQueryKey(METHOD, ENDPOINT, params);

export const useGetInboxConversations = (
  params: ApiRequest_GetInboxConversations,
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    enabled: !!params.location_id && enabled && !!session?.access_token,
    queryKey: QUERY_KEY(params),
    queryFn: async (): Promise<UnifiedInboxConversation[]> => {
      // Fetch unified conversations from the new single endpoint
      const data = await handleApiCall<
        ApiResponse_GetInboxConversations,
        ApiRequest_GetInboxConversations
      >(session, METHOD, getResolvedEndpoint(ENDPOINT, {}), params);

      // The backend now returns the data with type already set and sorted by timestamp
      return data;
    },
  });
};
